import { initAppConfig, Providers } from '@custom/ui/routes/Providers';
import { GatsbyBrowser, WrapPageElementBrowserArgs } from 'gatsby';
import React, { useEffect, useState } from 'react';

import { i18n, Locale, locales } from '../../i18n';
import { APP_CONFIG, DOMAINS } from '../constants';
import { Layout } from '../layout/layout';
import { PageContext } from '../types';

initAppConfig(APP_CONFIG as any);

export const WrapPageElement: GatsbyBrowser['wrapPageElement'] = ({
  element,
  props,
}: WrapPageElementBrowserArgs<any, PageContext>) => {
  // WrapPageElement does not allow hooks, so we have to use a wrapper.
  // See https://github.com/gatsbyjs/gatsby/issues/22833
  return <Wrapper element={element} props={props} />;
};

const Wrapper = ({
  element,
  props,
}: {
  element: React.ReactElement;
  props: WrapPageElementBrowserArgs<any, PageContext>['props'];
}) => {
  const [locale, setLocale] = useState(props.pageContext.locale);
  const [localizations, setLocalizations] = useState(
    props.pageContext.localizations,
  );

  useEffect(() => {
    // For the 404 page use the browser URL to
    // - Determine the locale
    // - Generate localizations
    const is404Page = 'pagePath' in window && window.pagePath === '/404.html';
    if (is404Page) {
      const { domainPrefix, langcode, path } = parsePath(
        window.location.pathname,
      );
      setLocale(langcode);
      setLocalizations(
        Object.values(locales).map((locale) => ({
          path: `${domainPrefix}/${locale.code}${path}`,
          locale: locale.code,
        })),
      );
    } else {
      // For other pages, use values from props.
      setLocale(props.pageContext.locale);
      setLocalizations(props.pageContext.localizations);
    }
  }, [
    // Re-run the effect on routing.
    props.location.pathname,
    props.pageContext.locale,
    props.pageContext.localizations,
  ]);

  return (
    <Providers
      domain={(props.pageContext.domain?.id || 'paraplegie_ch') as any}
      i18n={i18n}
      path={props.path}
      locale={locale as Locale}
      localizations={localizations as Array<{ path: string; locale: Locale }>}
    >
      <Layout>{element}</Layout>
    </Providers>
  );
};

function parsePath(path: string): {
  domainPrefix: string;
  langcode: Locale;
  path: string;
} {
  const langcodes = Object.values(locales).map((locale) => locale.code);
  const domainPrefixes = Object.values(DOMAINS)
    .map((domain) => domain.pathPrefix)
    .filter(Boolean);

  const segments = path.split('/').filter(Boolean);

  let domainPrefix = '';
  if (segments[0] && domainPrefixes.includes('/' + segments[0])) {
    domainPrefix = '/' + segments.shift();
  }

  let langcode = langcodes[0];
  if (segments[0] && langcodes.includes(segments[0] as Locale)) {
    langcode = segments.shift() as Locale;
  }

  return {
    domainPrefix,
    langcode,
    path: '/' + segments.join('/'),
  };
}
